// components/SendNotification.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { RadioButton } from 'primereact/radiobutton';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { Image } from 'primereact/image';
import { Chip } from 'primereact/chip';
import axios from 'axios';
import NotificationService from '../../service/NotificationService';

const notificationService = new NotificationService();

const SendNotification = () => {
    const navigate = useHistory();
    const toast = useRef(null);
    const fileUploadRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    const iss = localStorage.getItem('iss');

    const [formData, setFormData] = useState({
        title: '',
        body: '',
        image: null,
        targetAudience: 'all', // 'all' ou 'specific'
        userIds: [],
        data: {} // Dados customizados (opcional)
    });

    const [errors, setErrors] = useState({});

    // Carregar lista de usuários (para seleção específica)
    useEffect(() => {
        if (formData.targetAudience === 'specific') {
            loadUsers();
        }
    }, [formData.targetAudience]);

    const loadUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await axios.get('/api/v1/users', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });

            // Formatar usuários para o MultiSelect
            const formattedUsers = response.data.users?.map(user => ({
                label: `${user.name} (${user.email})`,
                value: user.id
            })) || [];

            setUsers(formattedUsers);
        } catch (error) {
            console.error('❌ Erro ao carregar usuários:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro ao carregar lista de usuários',
                life: 3000
            });
        } finally {
            setLoadingUsers(false);
        }
    };

    // Validação do formulário
    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'O título é obrigatório';
        } else if (formData.title.length > 100) {
            newErrors.title = 'O título não pode ter mais de 100 caracteres';
        }

        if (!formData.body.trim()) {
            newErrors.body = 'A mensagem é obrigatória';
        } else if (formData.body.length > 500) {
            newErrors.body = 'A mensagem não pode ter mais de 500 caracteres';
        }

        if (formData.targetAudience === 'specific' && formData.userIds.length === 0) {
            newErrors.userIds = 'Selecione pelo menos um usuário';
        }

        if (formData.image && formData.image.size > 5 * 1024 * 1024) {
            newErrors.image = 'A imagem não pode ter mais de 5MB';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handler de mudança de campos
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpar erro do campo quando o usuário começar a digitar
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Handler de seleção de imagem
    const onImageSelect = (e) => {
        const file = e.files[0];

        if (file) {
            // Validar tamanho
            if (file.size > 5 * 1024 * 1024) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'A imagem não pode ter mais de 5MB',
                    life: 3000
                });
                fileUploadRef.current.clear();
                return;
            }

            // Validar tipo
            if (!file.type.startsWith('image/')) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Apenas arquivos de imagem são permitidos',
                    life: 3000
                });
                fileUploadRef.current.clear();
                return;
            }

            setFormData(prev => ({ ...prev, image: file }));

            // Gerar preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Remover imagem
    const removeImage = () => {
        setFormData(prev => ({ ...prev, image: null }));
        setImagePreview(null);
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    // Enviar notificação
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Por favor, corrija os erros no formulário',
                life: 3000
            });
            return;
        }

        setLoading(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('body', formData.body);

            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }

            if (formData.targetAudience === 'specific') {
                formDataToSend.append('userIds', JSON.stringify(formData.userIds));
            }

            if (Object.keys(formData.data).length > 0) {
                formDataToSend.append('data', JSON.stringify(formData.data));
            }

            /* const profileId = localStorage.getItem('profileId'); // Ajustar conforme seu sistema

            const response = await axios.post(
                `/api/v1/${profileId}/notifications`,
                formDataToSend,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${localStorage.getItem('token')}`
                    }
                }
            ); */

            const response = await notificationService.enviarNotificacao(iss, formDataToSend);

            console.log('✅ Resposta:', response.data);

            toast.current?.show({
                severity: 'success',
                summary: 'Sucesso',
                detail: `Notificação enviada para ${response.data.delivery.summary.totalSuccess} dispositivos`,
                life: 5000
            });

            // Aguardar 2 segundos e redirecionar
            setTimeout(() => {
                navigate.push('/notificacoes/listar');
            }, 2000);

        } catch (error) {
            console.error('❌ Erro ao enviar notificação:', error);

            const errorMessage = error.response?.data?.error ||
                error.response?.data?.details ||
                'Erro ao enviar notificação';

            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: errorMessage,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    // Resetar formulário
    const handleReset = () => {
        setFormData({
            title: '',
            body: '',
            image: null,
            targetAudience: 'all',
            userIds: [],
            data: {}
        });
        setImagePreview(null);
        setErrors({});
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    // ==================== RENDER ====================

    return (
        <div className="card">
            <Toast ref={toast} />

            {/* Header */}
            <div className="flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="m-0">📤 Enviar Nova Notificação</h2>
                    <p className="text-500 mt-1">Crie e envie notificações push para seus usuários</p>
                </div>
                <Button
                    label="Voltar"
                    icon="pi pi-arrow-left"
                    className="p-button-text"
                    onClick={() => navigate.push('/notificacoes/listar')}
                />
            </div>

            <form onSubmit={handleSubmit}>
                <div className="grid">
                    {/* Coluna Esquerda - Formulário */}
                    <div className="col-12 lg:col-8">
                        <Card title="📝 Conteúdo da Notificação">
                            {/* Título */}
                            <div className="field mb-4">
                                <label htmlFor="title" className="block font-semibold mb-2">
                                    Título <span className="text-red-500">*</span>
                                </label>
                                <InputText
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="Ex: Promoção Especial!"
                                    className={`w-full ${errors.title ? 'p-invalid' : ''}`}
                                    maxLength={100}
                                />
                                <small className="block mt-1 text-500">
                                    {formData.title.length}/100 caracteres
                                </small>
                                {errors.title && (
                                    <small className="block mt-1 p-error">{errors.title}</small>
                                )}
                            </div>

                            {/* Mensagem */}
                            <div className="field mb-4">
                                <label htmlFor="body" className="block font-semibold mb-2">
                                    Mensagem <span className="text-red-500">*</span>
                                </label>
                                <InputTextarea
                                    id="body"
                                    value={formData.body}
                                    onChange={(e) => handleChange('body', e.target.value)}
                                    placeholder="Digite a mensagem da notificação..."
                                    rows={5}
                                    className={`w-full ${errors.body ? 'p-invalid' : ''}`}
                                    maxLength={500}
                                />
                                <small className="block mt-1 text-500">
                                    {formData.body.length}/500 caracteres
                                </small>
                                {errors.body && (
                                    <small className="block mt-1 p-error">{errors.body}</small>
                                )}
                            </div>

                            <Divider />

                            {/* Upload de Imagem */}
                            <div className="field mb-4">
                                <label className="block font-semibold mb-2">
                                    Imagem (Opcional)
                                </label>

                                {!imagePreview ? (
                                    <FileUpload
                                        ref={fileUploadRef}
                                        mode="basic"
                                        name="image"
                                        accept="image/*"
                                        maxFileSize={5000000}
                                        onSelect={onImageSelect}
                                        chooseLabel="Selecionar Imagem"
                                        className="w-full"
                                        auto={false}
                                    />
                                ) : (
                                    <div className="border-round border-1 border-300 p-3">
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <span className="font-semibold">Imagem Selecionada:</span>
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-rounded p-button-danger p-button-text"
                                                onClick={removeImage}
                                                type="button"
                                            />
                                        </div>
                                        <Image
                                            src={imagePreview}
                                            alt="Preview"
                                            width="100%"
                                            preview
                                            imageStyle={{ borderRadius: '8px', maxHeight: '300px', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}

                                <small className="block mt-1 text-500">
                                    Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
                                </small>
                                {errors.image && (
                                    <small className="block mt-1 p-error">{errors.image}</small>
                                )}
                            </div>

                            {/* <Divider /> */}

                            {/* Público Alvo */}
                            {/* <div className="field mb-4">
                                <label className="block font-semibold mb-3">
                                    Público Alvo <span className="text-red-500">*</span>
                                </label>

                                <div className="flex flex-column gap-3">
                                    <div className="flex align-items-center">
                                        <RadioButton
                                            inputId="all"
                                            name="targetAudience"
                                            value="all"
                                            onChange={(e) => handleChange('targetAudience', e.value)}
                                            checked={formData.targetAudience === 'all'}
                                        />
                                        <label htmlFor="all" className="ml-2 cursor-pointer">
                                            <div className="font-semibold">Todos os Usuários</div>
                                            <div className="text-sm text-500">Enviar para todos os dispositivos cadastrados</div>
                                        </label>
                                    </div>

                                    <div className="flex align-items-center">
                                        <RadioButton
                                            inputId="specific"
                                            name="targetAudience"
                                            value="specific"
                                            onChange={(e) => handleChange('targetAudience', e.value)}
                                            checked={formData.targetAudience === 'specific'}
                                        />
                                        <label htmlFor="specific" className="ml-2 cursor-pointer">
                                            <div className="font-semibold">Usuários Específicos</div>
                                            <div className="text-sm text-500">Selecionar usuários manualmente</div>
                                        </label>
                                    </div>
                                </div> */}

                            {/* MultiSelect de Usuários */}
                            {/* {formData.targetAudience === 'specific' && (
                                    <div className="mt-3">
                                        <MultiSelect
                                            value={formData.userIds}
                                            options={users}
                                            onChange={(e) => handleChange('userIds', e.value)}
                                            placeholder="Selecione os usuários"
                                            filter
                                            showClear
                                            className={`w-full ${errors.userIds ? 'p-invalid' : ''}`}
                                            display="chip"
                                            loading={loadingUsers}
                                            emptyFilterMessage="Nenhum usuário encontrado"
                                            maxSelectedLabels={3}
                                        />
                                        {formData.userIds.length > 0 && (
                                            <small className="block mt-1 text-500">
                                                {formData.userIds.length} usuário(s) selecionado(s)
                                            </small>
                                        )}
                                        {errors.userIds && (
                                            <small className="block mt-1 p-error">{errors.userIds}</small>
                                        )}
                                    </div>
                                )}
                            </div> */}
                        </Card>
                    </div>

                    {/* Coluna Direita - Preview */}
                    <div className="col-12 lg:col-4">
                        <Card title="📱 Preview da Notificação" className="sticky" style={{ top: '20px' }}>
                            <Message
                                severity="info"
                                text="Visualização aproximada de como a notificação aparecerá no dispositivo"
                                className="mb-3"
                            />

                            {/* Preview Mobile */}
                            <div
                                className="border-round-lg p-3"
                                style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6'
                                }}
                            >
                                <div className="flex align-items-start gap-2">
                                    {/* Ícone do App */}
                                    <div
                                        className="flex-shrink-0"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#3b82f6',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <i className="pi pi-bell" style={{ color: 'white', fontSize: '1.2rem' }}></i>
                                    </div>

                                    {/* Conteúdo */}
                                    <div className="flex-grow-1">
                                        <div className="flex justify-content-between align-items-start mb-1">
                                            <span className="font-semibold text-sm">Seu App</span>
                                            <span className="text-xs text-500">agora</span>
                                        </div>

                                        <div className="font-bold mb-1" style={{ fontSize: '0.95rem' }}>
                                            {formData.title || 'Título da notificação'}
                                        </div>

                                        <div className="text-sm text-600 mb-2">
                                            {formData.body || 'Mensagem da notificação aparecerá aqui...'}
                                        </div>

                                        {/* Preview da Imagem */}
                                        {imagePreview && (
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                style={{
                                                    width: '100%',
                                                    borderRadius: '8px',
                                                    marginTop: '8px',
                                                    maxHeight: '150px',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <Divider />

                            {/* Informações Adicionais */}
                            <div className="flex flex-column gap-2">
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-500">Público:</span>
                                    <Chip
                                        label={formData.targetAudience === 'all' ? 'Todos' : `${formData.userIds.length} usuários`}
                                        icon={formData.targetAudience === 'all' ? 'pi pi-users' : 'pi pi-user'}
                                    />
                                </div>

                                <div className="flex justify-content-between mb-2">
                                    <span className="text-500">Imagem:</span>
                                    <Chip
                                        label={imagePreview ? 'Sim' : 'Não'}
                                        icon={imagePreview ? 'pi pi-check' : 'pi pi-times'}
                                    />
                                </div>

                                <div className="flex justify-content-between">
                                    <span className="text-500">Caracteres:</span>
                                    <span className="font-semibold">
                                        {formData.title.length + formData.body.length}/600
                                    </span>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Botões de Ação */}
                <Card className="mt-4">
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Limpar"
                            icon="pi pi-refresh"
                            className="p-button-secondary"
                            onClick={handleReset}
                            type="button"
                            disabled={loading}
                        />
                        <Button
                            label="Enviar Notificação"
                            icon="pi pi-send"
                            className="p-button-success ml-3"
                            type="submit"
                            loading={loading}
                        />
                    </div>

                    {loading && (
                        <div className="mt-3">
                            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
                            <p className="text-center text-500 mt-2">Enviando notificação...</p>
                        </div>
                    )}
                </Card>
            </form>
        </div>
    );
};

export default SendNotification;